import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Person } from "../../models/person";
import { Supplier } from "../../models/supplier";
import { set } from "lodash";
import { all } from "axios";
import CopyButton from "../../components/ui/CopyButton";
import ReactModal from "react-modal";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { 
  getProfiles as getProfilesAction, 
  getFQMProfiles,
  selectCombinedProfiles,
  selectProfilesStatus,
  selectProfilesError
} from "../../reducers/profileSlice";

interface TeamSettings {
  name: string;
  description: string;
  settings: {
    name: string;
    options: {
      name: string;
      checked: () => boolean;
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    }[];
  }[];
}

export default function TeamSettings(props: {
  selectedSupplier: Supplier[];
}): JSX.Element {
  const dispatch = useAppDispatch();
  const combinedProfiles = useAppSelector(selectCombinedProfiles);
  const profilesStatus = useAppSelector(selectProfilesStatus);
  const profilesError = useAppSelector(selectProfilesError);
  
  const [search, setSearch] = useState<string>("");
  const [allContacts, setAllContacts] = useState<Person[]>([] as Person[]);
  const [filteredContacts, setFilteredContacts] = useState<Person[]>(
    [] as Person[],
  );

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Person>(
    null as any as Person,
  );
  const [searchParams] = useSearchParams();
  const [newContact, setNewCoontact] = useState<Person>({} as Person);
  const [isModalOpen, setModalIsOpen] = useState(false);
  const [desc, setDesc] = useState<string>("");
  const [saveButtonText, setSaveButtonText] = useState("Save");

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        await Promise.all([
          dispatch(getProfilesAction()),
          dispatch(getFQMProfiles())
        ]);
      } catch (error) {
        console.error('Error loading profiles:', error);
      }
    };

    setTimeout(() => {
      loadProfiles();
    }, 0);
  }, [dispatch]);

  useEffect(() => {
    if (!props.selectedSupplier) {
      setFilteredContacts([] as Person[]);
      setAllContacts([] as Person[]); // Also reset allContacts
      return;
    }

    if (!combinedProfiles?.length) {
      setSelectedProfile(null as any as Person);
      return;
    }

    const profile = combinedProfiles.find(
      (p) => p.supplier === props.selectedSupplier[0]?.id
    ) || (null as any as Person);

    setSelectedProfile(profile);

    const allContacts = props.selectedSupplier.flatMap(
      (supplier) => supplier.contacts ?? [],
    );

    setAllContacts(allContacts);
    setFilteredContacts(allContacts);

    setInitialLoadComplete(true);
  }, [props.selectedSupplier, combinedProfiles]);

  useEffect(() => {
    if (!search) {
      setFilteredContacts(allContacts);
      return;
    }
    searchContacts(search);
  }, [search]);

  const onAddContactClicked = () => {
    let newTempContact = {} as Person;
    newTempContact.qaCreation = true;
    newTempContact.qaUpdate = true;
    newTempContact.plantShutdown = true;
    newTempContact.weekendProduction = true;
    newTempContact.weeklySummary = true;
    newTempContact.dailySummary = true;
    newTempContact.parentSummary = true;
    newTempContact.additionalHours = true;
    setNewCoontact(newTempContact);
    setModalIsOpen(true);
    ``;
  };

  const searchContacts = (
    value: string,
    contactData: Person[] = allContacts,
  ) => {
    setSearch(value);
    const data = contactData.filter((l: Person) => checkSearch(l));
    setFilteredContacts(data);
  };

  const checkSearch = (contact: Person) => {
    return (
      contact?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      contact?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      contact?.email?.toLowerCase().includes(search.toLowerCase())
    );
  };

  function formatNumber(number: string): string {
    if (number === undefined || number === null) return "N/A";

    const cleaned = number.replace(/\D/g, "");
    const formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    return formatted;
  }

  const textAreaChanged = (value: string) => {
    setDesc(value);
  };

  const supplierSelected = props.selectedSupplier.length > 0 && initialLoadComplete;
  
  if (!supplierSelected) {
    return <></>;
  }
  
  return (
    <div className="flex h-[calc(100%-151px)] w-full flex-col overflow-hidden">
      <div
        className="flex w-full flex-row border-b-[1px] border-b-[rgba(166,166,166,0.3)] py-[20px] h-[97px] items-baseline"
        key="team"
      >
        <div className="relative mt-[4px] flex w-full items-center">
          <div className="pr-6">
            <span className="text-[12px] font-semibold text-tqsBlack">
              Search
            </span>
          </div>
          <div className="relative w-[30%]">
            {/* Magnifying Glass Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70"
            >
              <path
                fillRule="evenodd"
                d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
                clipRule="evenodd"
              />
            </svg>

            {/* Input Field */}
            <input
              className="text-tqs placeholder-gray h-[45px] w-full rounded-md border border-gray-300 bg-white p-1 pl-[40px] pr-[40px] text-[15px] font-medium"
              type="text"
              placeholder="Search Name or Email"
              value={search}
              onChange={(e) => setSearch(e.target.value)} // Update the search term state
            />

            {/* "X" Icon */}
            {search && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#212322"
                className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform cursor-pointer text-gray-400"
                onClick={() => setSearch("")} // Clear the search term when the icon is clicked
              >
                <path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        <div
          className="grid mt-[4px] h-[45px] w-32 cursor-pointer justify-items-center rounded border-0 bg-yellow-300 text-sm font-semibold text-black hover:bg-gray-300"
          onClick={() =>
            window.open(
              "https://form.jotform.com/252804855743161",
              "_blank",
            )
          }
        >
          <span className="self-center justify-self-center">+ Add Contact</span>
        </div>
      </div>
      <div className="h-[calc(100%-97px)] overflow-scroll">
        <div className="">
          <table className="min-w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-[0px] z-[1]">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Your Team
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredContacts.length > 0 &&
                filteredContacts.map((contact, index) => (
                  <tr key={index}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      <div className="flex min-w-0 flex-1 items-center">
                        <img
                          className="mr-3 h-8 w-8 rounded-full"
                          src={
                            contact.photoAttachment
                              ? contact.photoAttachment
                              : "/dashboard/avatar.png"
                          }
                          alt={`${contact?.firstName} ${contact?.lastName}`}
                        />
                        <div>
                          <p className="truncate text-sm font-medium text-tqsBlack">
                            {contact?.firstName} {contact?.lastName} {localStorage.getItem('userEmail') == contact?.email && <span className="truncate text-sm font-medium text-gray-400">(ME)</span>}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      <div className="ml-6 flex flex-1 items-center">
                        <a
                          href={`mailto:${contact?.email}`}
                          className="mr-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#73cffd] text-white"
                          title="Mail"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="h-[14px] w-[14px]"
                          >
                            <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                            <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                          </svg>
                        </a>
                        <CopyButton text={contact?.email}>
                          <span className="whitespace-nowrap py-1.5 text-xs font-medium text-gray-400">
                            {contact?.email}
                          </span>
                        </CopyButton>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-1 items-center">
                        <button
                          className="ml-4 mr-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#3a7bfc] text-white"
                          title="Phone"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="h-[14px] w-[14px]"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97ZM6.75 8.25a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H7.5Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <CopyButton text={contact?.cell ?? "N/A"}>
                          <span className="whitespace-nowrap py-1.5 text-xs font-medium text-gray-400">
                            {formatNumber(contact?.cell) ?? "N/A"}
                          </span>
                        </CopyButton>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredContacts.length <= 0 && 
                  <tr>
                    <td colSpan={3} className="text-center text-[20px] font-bold text-tqsBlack">There are currently no users matching your criteria.</td>
                  </tr>
                }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
